import React from 'react'
import { Department } from 'enums/department'
import {
  Box,
  Snackbar,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  TablePagination,
} from '@mui/material'
import MuiAlert from '@mui/material/Alert'

import API from '@aws-amplify/api'
import { useDispatch } from 'react-redux'
import { formatTimestamp } from 'shared/dateFormatter'
import SmoothLoadingIndicator from 'components/shared/SmoothLoadingIndicator'
import ConfirmDialog from 'components/confirmDialog'
import { getFriendlyErrorMessage } from 'utils/errorHandling'
import { IProduction, Production } from 'interfaces/IProduction'
import {
  STOCK_CARD_TYPE_HALF_COMPLETE,
  STOCK_CARD_TYPE_PACKAGING,
  STOCK_CARD_TYPE_PREMIX,
} from 'enums/stockCard'

export default function FillingList(props: { department: Department }) {
  const { department } = props
  const [isComplete, setIsComplete] = React.useState(false)
  const [editedQuantityMade, setEditedQuantityMade] = React.useState<{
    [batchNumber: string]: string
  }>({})
  const [editedFillingAmounts, setEditedFillingAmounts] = React.useState<{
    [batchNumber: string]: string
  }>({})

  const [productionData, setProductionData] = React.useState<IProduction>(
    new Production({})
  )
  const [productions, setProductions] = React.useState<IProduction[]>([])
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [alertMessage, setAlertMessage] = React.useState('')
  const [isConfirmingFillingComplete, setIsConfirmingFillingComplete] =
    React.useState(false)
  const [premixDepartments, setPremixDepartments] = React.useState<{
    [premixId: string]: string
  }>({})

  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(10)
  const [totalCount, setTotalCount] = React.useState(0)
  const [isConfirmingCancelFilling, setIsConfirmingCancelFilling] =
    React.useState(false)

  // Set default values when dialog opens
  React.useEffect(() => {
    if (isConfirmingFillingComplete && productionData.batches) {
      setEditedFillingAmounts(
        productionData.batches.reduce((acc, batch) => {
          acc[batch.batchNumber] =
            batch.fillingAmount !== undefined && batch.fillingAmount !== null
              ? String(batch.fillingAmount)
              : ''
          return acc
        }, {} as { [batchNumber: string]: string })
      )
      setEditedQuantityMade(
        productionData.batches.reduce((acc, batch) => {
          acc[batch.batchNumber] =
            batch.packagingAmount !== undefined &&
              batch.packagingAmount !== null
              ? String(batch.packagingAmount)
              : ''
          return acc
        }, {} as { [batchNumber: string]: string })
      )
    }
  }, [isConfirmingFillingComplete, productionData])

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }

  const handleDialogClose = () => {
    setIsConfirmingFillingComplete(false)
    setIsConfirmingCancelFilling(false)
    setProductionData(new Production({}))
  }

  const getProductions = React.useCallback(
    (
      isCompleteParam: boolean = isComplete,
      pageParam: number = page,
      pageSizeParam: number = rowsPerPage
    ) => {
      setIsLoading(true)
      setProductions([])
      API.get('products', '/production/getProductions', {
        queryStringParameters: {
          type: STOCK_CARD_TYPE_HALF_COMPLETE,
          isComplete: isCompleteParam,
          page: pageParam + 1,
          pageSize: pageSizeParam,
          department,
        },
      })
        .then((response) => {
          setProductions(response.data)
          setTotalCount(response.totalCount)
        })
        .catch((error) => {
          setIsFailureSnackbarOpen(true)
          setAlertMessage('Gagal memuat form produksi')
          console.error(error.response)
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [isComplete, page, rowsPerPage, department]
  )

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
    getProductions(isComplete, newPage, rowsPerPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
    getProductions(isComplete, 0, newRowsPerPage)
  }

  const dispatch = useDispatch()
  React.useEffect(() => {
    const title = department
      ? `Daftar Filling (${department.toUpperCase()})`
      : 'Filling'
    dispatch({
      type: 'SET_PAGE_TITLE',
      payload: title,
    })
  }, [dispatch, department])

  React.useEffect(() => {
    setPage(0)
    getProductions(isComplete, 0, rowsPerPage)
  }, [isComplete, getProductions, rowsPerPage])

  React.useEffect(() => {
    const fetchDepartments = async () => {
      const newPremixIds = productions
        .map((p) => p.premixId)
        .filter((id) => id && !premixDepartments[id])

      const uniqueIds = Array.from(new Set(newPremixIds))

      if (uniqueIds.length > 0) {
        const results = await Promise.all(
          uniqueIds.map((id) =>
            API.get('products', `/premix/getPremix/${id}`, {})
              .then((res) => ({
                id,
                department: res[0]?.department ?? 'KOSMETIK',
              }))
              .catch(() => ({ id, department: 'KOSMETIK' }))
          )
        )
        setPremixDepartments((prev) => {
          const next = { ...prev }
          results.forEach((r) => (next[r.id] = r.department))
          return next
        })
      }
    }
    fetchDepartments()
  }, [productions, premixDepartments])

  const isStockCardConfirmed = (production: IProduction) => {
    const stockCardPackaging = production.stockCards?.find(
      (stockCard) => stockCard.type === STOCK_CARD_TYPE_PACKAGING
    )
    const stockCardPremix = production.stockCards?.find(
      (stockCard) => stockCard.type === STOCK_CARD_TYPE_PREMIX
    )
    return stockCardPremix?.isConfirmed && stockCardPackaging?.isConfirmed
  }

  const handleFillingComplete = async () => {
    const updatedProduction = {
      ...productionData,
      batches: productionData.batches.map((batch) => ({
        ...batch,
        confirmedFillingAmount: editedFillingAmounts[batch.batchNumber],
        quantityMade: editedQuantityMade[batch.batchNumber],
      })),
    }

    try {
      await API.post('products', `/production/handleFillingComplete`, {
        body: updatedProduction,
      })
      getProductions()
    } catch (error) {
      setAlertMessage(getFriendlyErrorMessage(error, 'Gagal menandai filling selesai.'))
      setIsFailureSnackbarOpen(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancelFillingComplete = async (production: IProduction) => {
    setIsLoading(true)
    try {
      await API.post('products', `/production/cancelFillingComplete`, {
        body: { productionId: production.productionId },
      })
      setAlertMessage('Aksi filling berhasil dibatalkan')
      setIsSuccessSnackbarOpen(true)
      getProductions()
    } catch (error) {
      console.error(error)
      setAlertMessage('Gagal membatalkan aksi filling')
      setIsFailureSnackbarOpen(true)
    } finally {
      setIsLoading(false)
    }
  }

  // Helper to get button label for filling action
  const getFillingButtonLabel = (production: IProduction) => {
    if (isStockCardConfirmed(production)) {
      if (production.isComplete) {
        return 'Menunggu Diterima Gudang STJ'
      }
      return 'Tandai selesai filling'
    }
    const unconfirmed =
      production.stockCards?.filter((sc) => !sc.isConfirmed) ?? []
    if (unconfirmed.length) {
      return `Tunggu STJ Dikonfirmasi: ${unconfirmed
        .map((sc: any) => sc.stockCardDisplayId)
        .join(', ')}`
    }
    return 'Menunggu instruksi Production Manager ke gudang ruahan & kemasan.'
  }

  return (
    <Box>
      <React.Fragment>
        <Box mt={3} />
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            variant={!isComplete ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => setIsComplete(false)}
            style={{ marginRight: 8 }}
          >
            Aktif
          </Button>
          <Button
            variant={isComplete ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => setIsComplete(true)}
          >
            Riwayat
          </Button>
        </Box>
      </React.Fragment>
      <Paper>
        {isLoading ? (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            style={{ minHeight: 400 }}
          >
            <SmoothLoadingIndicator size={120} />
          </Grid>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableCell>Kode Permintaan Setengah Jadi</TableCell>
                <TableCell>Permintaan Filling</TableCell>
                <TableCell>Action</TableCell>
              </TableHead>
              <TableBody>
                {productions.map((production) => (
                  <TableRow key={production.productionDisplayId}>
                    <TableCell>
                      <Typography>{production.productionDisplayId}</Typography>
                      <Typography>
                        {formatTimestamp('id-ID', production.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {production.premixId} - {production.premixName}
                      </Typography>
                      <Typography>
                        {production.packagingId} - {production.packagingName}
                      </Typography>
                      {production.batches.map((batch) => (
                        <React.Fragment key={batch.batchNumber}>
                          <Typography>
                            <Box fontWeight="bold" display="inline">
                              No. Bets {batch.batchNumber}
                            </Box>
                          </Typography>
                          <Typography>
                            Jumlah Bets Diminta: {batch.quantity}{' '}
                            {premixDepartments[production.premixId]?.toLowerCase() === 'ot'
                              ? 'KAPSUL'
                              : 'KG'}
                          </Typography>
                          <Typography>
                            Filling Amount Diminta: {batch.fillingAmount}{' '}
                            {premixDepartments[production.premixId]?.toLowerCase() === 'ot'
                              ? 'KAPSUL'
                              : 'KG'}
                          </Typography>
                          <Typography>
                            Jumlah Kemasan Diminta:{' '}
                            {Number(batch.packagingAmount)} pcs
                          </Typography>
                        </React.Fragment>
                      ))}
                      {production.stockCards &&
                        production.stockCards.length > 0 && (
                          <Box mt={1}>
                            <Typography variant="caption" color="textSecondary">
                              Dokumen Terkait:{' '}
                              {production.stockCards
                                .map(
                                  (sc) =>
                                    `${sc.stockCardDisplayId}${sc.isConfirmed
                                      ? ' (Sudah Konfirmasi)'
                                      : ' (Belum Konfirmasi)'
                                    }`
                                )
                                .join(', ')}
                            </Typography>
                          </Box>
                        )}
                    </TableCell>
                    <TableCell>
                      {!production.isComplete && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            setIsConfirmingFillingComplete(true)
                            setProductionData(production)
                          }}
                          disabled={!isStockCardConfirmed(production) || isLoading}
                        >
                          {getFillingButtonLabel(production)}
                        </Button>
                      )}
                      {production.isComplete && (
                        <Box>
                          <Box display="flex" alignItems="center">
                            {production.stockCards?.some(
                              (sc) =>
                                sc.type === STOCK_CARD_TYPE_HALF_COMPLETE &&
                                sc.isConfirmed
                            ) && (
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  style={{ marginRight: 8 }}
                                >
                                  Sudah dikonfirmasi gudang STJ
                                </Typography>
                              )}
                            <Button
                              variant="outlined"
                              color="secondary"
                              disabled={production.stockCards?.some(
                                (sc) =>
                                  sc.type === STOCK_CARD_TYPE_HALF_COMPLETE &&
                                  sc.isConfirmed
                              )}
                              onClick={() => {
                                setIsConfirmingCancelFilling(true)
                                setProductionData(production)
                              }}
                            >
                              CANCEL TANDAI SELESAI FILLING
                            </Button>
                          </Box>
                          {production.stockCards &&
                            production.stockCards.length > 0 && (
                              <Box mt={1}>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  Dokumen Terkait:{' '}
                                  {production.stockCards
                                    .map(
                                      (sc) =>
                                        `${sc.stockCardDisplayId}${sc.isConfirmed
                                          ? ' (Sudah Konfirmasi)'
                                          : ' (Belum Konfirmasi)'
                                        }`
                                    )
                                    .join(', ')}
                                </Typography>
                              </Box>
                            )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}
      </Paper>
      <ConfirmDialog
        title={`Apakah yakin ingin menandai filling telah selesai?`}
        open={isConfirmingFillingComplete}
        setOpen={handleDialogClose}
        onConfirm={handleFillingComplete}
        confirmText="Simpan"
        cancelText="Batal"
      >
        <Typography>{productionData.productionDisplayId}</Typography>
        <Typography>
          {productionData.premixId} - {productionData.premixName}
        </Typography>
        <Typography>
          {productionData.packagingId} - {productionData.packagingName}
        </Typography>
        {productionData.batches.map((batch) => (
          <React.Fragment key={batch.batchNumber}>
            <Typography>
              <Box fontWeight="bold" display="inline">
                No. Bets {batch.batchNumber}
              </Box>
            </Typography>
            <Typography>
              Jumlah Bets Diminta: {batch.quantity}{' '}
              {premixDepartments[productionData.premixId]?.toLowerCase() === 'ot'
                ? 'KAPSUL'
                : 'KG'}
            </Typography>
            <Typography>
              Filling Amount Diminta: {Number(batch.fillingAmount)}{' '}
              {premixDepartments[productionData.premixId]?.toLowerCase() === 'ot'
                ? 'KAPSUL'
                : 'KG'}
            </Typography>
            <Typography>
              Jumlah Kemasan Diminta: {Number(batch.packagingAmount)} pcs
            </Typography>
            <Box display="flex" alignItems="center" mb={1} mt={2}>
              <Typography>Konfirmasi Filling Amount:</Typography>
              <TextField
                variant="outlined"
                size="small"
                style={{ width: 100 }}
                value={
                  editedFillingAmounts[batch.batchNumber] ??
                  batch.fillingAmount ??
                  ''
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditedFillingAmounts((prev) => ({
                    ...prev,
                    [batch.batchNumber]: e.target.value,
                  }))
                }
              />
              <Typography>
                {premixDepartments[productionData.premixId]?.toLowerCase() === 'ot'
                  ? 'KAPSUL'
                  : 'KG'}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" mb={1} mt={2}>
              <Typography>Quantity STJ Terbuat:</Typography>
              <TextField
                variant="outlined"
                size="small"
                style={{ width: 100 }}
                value={editedQuantityMade[batch.batchNumber] ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditedQuantityMade((prev) => ({
                    ...prev,
                    [batch.batchNumber]: e.target.value,
                  }))
                }
              />{' '}
              Pcs
            </Box>
          </React.Fragment>
        ))}
      </ConfirmDialog>
      <ConfirmDialog
        title={`Apakah yakin ingin membatalkan tanda selesai filling?`}
        open={isConfirmingCancelFilling}
        setOpen={handleDialogClose}
        onConfirm={() => handleCancelFillingComplete(productionData)}
        confirmText="Confirm Delete"
        cancelText="Batal"
      >
        <Typography>
          Ini akan menghapus kartu stok STJ masuk yang belum dikonfirmasi.
        </Typography>
        <Typography>{productionData.productionDisplayId}</Typography>
        <Typography>
          {productionData.premixId} - {productionData.premixName}
        </Typography>
        <Typography>
          {productionData.packagingId} - {productionData.packagingName}
        </Typography>
        {productionData.batches.map((batch) => (
          <React.Fragment key={batch.batchNumber}>
            <Typography>
              <Box fontWeight="bold" display="inline">
                No. Bets {batch.batchNumber}
              </Box>
            </Typography>
            <Typography>
              Konfirmasi Filling Amount: {Number(batch.fillingAmount)}{' '}
              {premixDepartments[productionData.premixId]?.toLowerCase() === 'ot'
                ? 'KAPSUL'
                : 'KG'}
            </Typography>
            <Typography>
              Quantity STJ Terbuat:{' '}
              {Number(batch.confirmedQuantity ?? batch.packagingAmount)} pcs
            </Typography>
          </React.Fragment>
        ))}
        {productionData.stockCards && productionData.stockCards.length > 0 && (
          <Box mt={1}>
            <Typography variant="caption" color="textSecondary">
              Dokumen Terkait:{' '}
              {productionData.stockCards
                .map(
                  (sc) =>
                    `${sc.stockCardDisplayId}${sc.isConfirmed
                      ? ' (Sudah Konfirmasi)'
                      : ' (Belum Konfirmasi)'
                    }`
                )
                .join(', ')}
            </Typography>
          </Box>
        )}
      </ConfirmDialog>
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
